import React, { useState, useEffect } from 'react';

const BillingSection = ({ user }) => {
  const [subscription, setSubscription] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadBillingData();
  }, []);

  const loadBillingData = async () => {
    try {
      // Mock data for now - in a real app, this would come from the API
      setSubscription({
        planName: 'Starter',
        status: 'Trial',
        trialEndsAt: new Date(Date.now() + 12 * 24 * 60 * 60 * 1000), // 12 days from now
        monthlyPrice: 0,
        nextBillingDate: new Date(Date.now() + 12 * 24 * 60 * 60 * 1000)
      });

      setUsage({
        users: { used: 1, limit: 5 },
        transactions: { used: 0, limit: 100 },
        xeroConnections: { used: 0, limit: 1 }
      });
    } catch (error) {
      console.error('Error loading billing data:', error);
    } finally {
      setLoading(false);
    }
  };

  const plans = [
    {
      name: 'Starter',
      monthlyPrice: 0,
      annualPrice: 0,
      features: ['Up to 5 users', '100 transactions/month', '1 Xero connection', 'Email support'],
      current: true
    },
    {
      name: 'Professional',
      monthlyPrice: 49,
      annualPrice: 490,
      features: ['Up to 20 users', '1,000 transactions/month', '3 Xero connections', 'Priority support', 'Advanced reporting'],
      popular: true
    },
    {
      name: 'Enterprise',
      monthlyPrice: 149,
      annualPrice: 1490,
      features: ['Unlimited users', 'Unlimited transactions', 'Unlimited Xero connections', '24/7 phone support', 'Custom integrations', 'Dedicated account manager']
    }
  ];

  const handleUpgrade = (planName) => {
    alert(`Upgrading to ${planName} plan - This would redirect to Stripe checkout in a real app!`);
  };

  const getDaysRemaining = () => {
    if (!subscription?.trialEndsAt) return 0;
    const days = Math.ceil((subscription.trialEndsAt - new Date()) / (1000 * 60 * 60 * 24));
    return Math.max(0, days);
  };

  const getUsagePercentage = (used, limit) => {
    return Math.round((used / limit) * 100);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Trial Status Alert */}
      {subscription?.status === 'Trial' && (
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <div className="text-2xl">⏰</div>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">
                Free Trial Active
              </h3>
              <div className="mt-2 text-sm text-blue-700">
                <p>You have <strong>{getDaysRemaining()} days</strong> left in your free trial. Upgrade now to continue using XeroPay after your trial ends.</p>
              </div>
              <div className="mt-4">
                <button
                  onClick={() => handleUpgrade('Professional')}
                  className="bg-blue-600 text-white px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium hover:bg-blue-700"
                >
                  Upgrade Now
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Current Usage */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
            Current Usage
          </h3>
          
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
            {/* Users Usage */}
            <div className="bg-gray-50 overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="text-2xl">👥</div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        Users
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {usage?.users.used} / {usage?.users.limit}
                      </dd>
                      <dd className="mt-1">
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-blue-600 h-2 rounded-full" 
                            style={{ width: `${getUsagePercentage(usage?.users.used || 0, usage?.users.limit || 1)}%` }}
                          ></div>
                        </div>
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            {/* Transactions Usage */}
            <div className="bg-gray-50 overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="text-2xl">📊</div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        Transactions
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {usage?.transactions.used} / {usage?.transactions.limit}
                      </dd>
                      <dd className="mt-1">
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-green-600 h-2 rounded-full" 
                            style={{ width: `${getUsagePercentage(usage?.transactions.used || 0, usage?.transactions.limit || 1)}%` }}
                          ></div>
                        </div>
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            {/* Xero Connections */}
            <div className="bg-gray-50 overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="text-2xl">🔗</div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        Xero Connections
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {usage?.xeroConnections.used} / {usage?.xeroConnections.limit}
                      </dd>
                      <dd className="mt-1">
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-purple-600 h-2 rounded-full" 
                            style={{ width: `${getUsagePercentage(usage?.xeroConnections.used || 0, usage?.xeroConnections.limit || 1)}%` }}
                          ></div>
                        </div>
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Subscription Plans */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-6">
            Subscription Plans
          </h3>
          
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
            {plans.map((plan) => (
              <div
                key={plan.name}
                className={`relative border rounded-lg p-6 ${
                  plan.popular
                    ? 'border-indigo-600 bg-indigo-50'
                    : plan.current
                    ? 'border-green-600 bg-green-50'
                    : 'border-gray-200'
                }`}
              >
                {plan.popular && (
                  <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-indigo-600 text-white">
                      Most Popular
                    </span>
                  </div>
                )}
                
                {plan.current && (
                  <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-600 text-white">
                      Current Plan
                    </span>
                  </div>
                )}

                <div className="text-center">
                  <h4 className="text-lg font-medium text-gray-900">{plan.name}</h4>
                  <div className="mt-4">
                    <span className="text-3xl font-bold text-gray-900">${plan.monthlyPrice}</span>
                    <span className="text-sm text-gray-500">/month</span>
                  </div>
                  {plan.annualPrice > 0 && (
                    <div className="text-sm text-gray-500">
                      or ${plan.annualPrice}/year (save ${(plan.monthlyPrice * 12 - plan.annualPrice)})
                    </div>
                  )}
                </div>

                <ul className="mt-6 space-y-3">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-center text-sm">
                      <div className="text-green-500 mr-2">✓</div>
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-6">
                  {plan.current ? (
                    <button
                      disabled
                      className="w-full bg-gray-100 text-gray-400 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium cursor-not-allowed"
                    >
                      Current Plan
                    </button>
                  ) : (
                    <button
                      onClick={() => handleUpgrade(plan.name)}
                      className={`w-full px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium ${
                        plan.popular
                          ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {subscription?.status === 'Trial' ? 'Upgrade' : 'Change Plan'}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Billing History */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
            Billing History
          </h3>
          
          <div className="text-center py-12">
            <div className="text-6xl mb-4">🧾</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No billing history yet</h3>
            <p className="text-gray-500">Your billing history will appear here once you start a paid subscription.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BillingSection;



























